
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { BrokerProfile } from '@/types/broker.types';

export const useBrokerProfile = () => {
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const [brokerProfile, setBrokerProfile] = useState<BrokerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    const fetchBrokerProfile = async () => {
      if (!user || userRole !== 'broker') return;
      
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('broker_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        setBrokerProfile(data);
      } catch (error: any) {
        console.error('Error fetching broker profile:', error);
        toast({
          title: 'Error fetching broker profile',
          description: error.message,
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBrokerProfile();
  }, [user, userRole, toast]);
  
  const updateBrokerProfile = async (updates: Partial<BrokerProfile>) => {
    if (!user || userRole !== 'broker' || !brokerProfile) return;
    
    try {
      const { error } = await supabase
        .from('broker_profiles')
        .update(updates)
        .eq('id', user.id);
        
      if (error) throw error;
      
      setBrokerProfile({
        ...brokerProfile,
        ...updates
      });
      
      toast({
        title: 'Profile updated',
        description: 'Your broker profile has been updated successfully',
      });
      
    } catch (error: any) {
      console.error('Error updating broker profile:', error);
      toast({
        title: 'Error updating profile',
        description: error.message,
        variant: 'destructive'
      });
    }
  };
  
  return {
    brokerProfile,
    isLoading,
    updateBrokerProfile
  };
};
